import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Skeleton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person,
  SupervisorAccount,
  Email,
  Phone,
  AdminPanelSettings,
  Assignment as AssignmentIcon,
  Home as HomeIcon,
  Remove as RemoveIcon,
  CleaningServices,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format, parseISO } from 'date-fns';
import { userApi, propertyApi, Property, PropertyAssignment } from '../services/api';
import { User } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/formatters';

// User form schema
const userSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().default(''),
  role: yup.string().oneOf(['admin', 'property_manager', 'staff', 'cleaner']).required('Role is required'),
  password: yup.string().default('').when('$isEdit', {
    is: false,
    then: (schema) => schema.required('Password is required').min(6, 'Password must be at least 6 characters'),
    otherwise: (schema) => schema.min(6, 'Password must be at least 6 characters'),
  }),
  password_confirmation: yup.string().default('').when('password', {
    is: (password: string) => password && password.length > 0,
    then: (schema) => schema.required('Password confirmation is required').oneOf([yup.ref('password')], 'Passwords must match'),
  }),
});

interface UserFormData {
  name: string;
  email: string;
  phone: string;
  role: 'admin' | 'property_manager' | 'staff' | 'cleaner';
  password: string;
  password_confirmation: string;
}

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [openPropertyAssignDialog, setOpenPropertyAssignDialog] = useState(false);
  const [openCleanerAssignDialog, setOpenCleanerAssignDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [assignmentType, setAssignmentType] = useState<'manager' | 'staff'>('staff');
  const [selectedProperties, setSelectedProperties] = useState<number[]>([]);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<UserFormData>({
    resolver: yupResolver(userSchema),
    context: { isEdit: !!editingUser },
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      role: 'staff',
      password: '',
      password_confirmation: '',
    },
  });

  // Fetch all users (admin only)
  const { data: users = [], isLoading: usersLoading, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: userApi.getAll,
    enabled: currentUser?.role === 'admin',
  });

  // Fetch all properties for assignment
  const { data: properties = [], isLoading: propertiesLoading } = useQuery({
    queryKey: ['properties'],
    queryFn: propertyApi.getAll,
    enabled: currentUser?.role === 'admin',
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: userApi.create,
    onSuccess: () => {
      refetchUsers();
      setOpenUserDialog(false);
      reset();
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<User> }) => userApi.update(id, data),
    onSuccess: () => {
      refetchUsers();
      setOpenUserDialog(false);
      setEditingUser(null);
      reset();
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: userApi.delete,
    onSuccess: () => {
      refetchUsers();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to delete user';
      alert(errorMessage);
    },
  });

  // Property assignment mutations
  const assignPropertyMutation = useMutation({
    mutationFn: ({ userId, propertyId, assignmentType }: { 
      userId: number; 
      propertyId: number; 
      assignmentType: 'manager' | 'staff' 
    }) => userApi.assignProperty(userId, propertyId, assignmentType),
    onSuccess: () => {
      refetchUsers();
      setOpenPropertyAssignDialog(false);
    },
  });

  const removePropertyMutation = useMutation({
    mutationFn: ({ userId, propertyId }: { userId: number; propertyId: number }) => 
      userApi.removePropertyAssignment(userId, propertyId),
    onSuccess: () => {
      refetchUsers();
    },
  });

  // Cleaner assignment mutation
  const assignCleanerMutation = useMutation({
    mutationFn: ({ userId, propertyIds }: { userId: number; propertyIds: number[] }) => 
      userApi.assignCleanerToProperties(userId, propertyIds),
    onSuccess: () => {
      refetchUsers();
      setOpenCleanerAssignDialog(false);
      setSelectedProperties([]);
    },
  });

  const handleOpenUserDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      reset({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        role: user.role,
        password: '',
        password_confirmation: '',
      });
    } else {
      setEditingUser(null);
      reset();
    }
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setEditingUser(null);
    reset();
  };

  const onSubmitUser = (data: UserFormData) => {
    const userData = {
      ...data,
      // Only include password if it's provided
      ...(data.password ? { password: data.password, password_confirmation: data.password_confirmation } : {}),
    };

    // Remove password_confirmation from the data sent to API
    const { password_confirmation, ...submitData } = userData;

    if (editingUser) {
      updateUserMutation.mutate({ id: editingUser.id, data: submitData });
    } else {
      // For new users, ensure password is provided
      if (!data.password) {
        alert('Password is required for new users');
        return;
      }
      createUserMutation.mutate(submitData as any);
    }
  };

  const handleDeleteUser = (userId: number) => {
    if (userId === currentUser?.id) {
      alert('You cannot delete your own account.');
      return;
    }
    
    if (currentUser?.role !== 'admin') {
      alert('Only administrators can delete users.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleOpenPropertyAssignDialog = (user: User) => {
    setSelectedUser(user);
    setAssignmentType(user.role === 'property_manager' ? 'manager' : 'staff');
    setOpenPropertyAssignDialog(true);
  };

  const handleClosePropertyAssignDialog = () => {
    setOpenPropertyAssignDialog(false);
    setSelectedUser(null);
  };

  const handleOpenCleanerAssignDialog = (user: User) => {
    setSelectedUser(user);
    // Pre-select already assigned properties
    setSelectedProperties(user.cleaner_properties?.map(p => p.id) || []);
    setOpenCleanerAssignDialog(true);
  };

  const handleCloseCleanerAssignDialog = () => {
    setOpenCleanerAssignDialog(false);
    setSelectedUser(null);
    setSelectedProperties([]);
  };

  const handleAssignProperty = (propertyId: number) => {
    if (!selectedUser) return;
    
    assignPropertyMutation.mutate({
      userId: selectedUser.id,
      propertyId,
      assignmentType
    });
  };

  const handleRemoveProperty = (userId: number, propertyId: number) => {
    if (window.confirm('Are you sure you want to remove this property assignment?')) {
      removePropertyMutation.mutate({ userId, propertyId });
    }
  };

  const handleAssignCleanerProperties = () => {
    if (!selectedUser) return;
    
    assignCleanerMutation.mutate({
      userId: selectedUser.id,
      propertyIds: selectedProperties
    });
  };

  const handlePropertySelectionChange = (propertyId: number, checked: boolean) => {
    if (checked) {
      setSelectedProperties(prev => [...prev, propertyId]);
    } else {
      setSelectedProperties(prev => prev.filter(id => id !== propertyId));
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'property_manager':
        return 'warning';
      case 'staff':
        return 'info';
      case 'cleaner':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettings />;
      case 'property_manager':
        return <SupervisorAccount />;
      case 'staff':
        return <Person />;
      case 'cleaner':
        return <CleaningServices />;
      default:
        return <Person />;
    }
  };

  const handleViewUser = (user: User) => {
    setViewingUser(user);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingUser(null);
  };

  // Check if current user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {t('access_denied')}
        </Alert>
      </Container>
    );
  }

  // Calculate statistics
  const adminUsers = users.filter(u => u.role === 'admin').length;
  const propertyManagerUsers = users.filter(u => u.role === 'property_manager').length;
  const staffUsers = users.filter(u => u.role === 'staff').length;
  const cleanerUsers = users.filter(u => u.role === 'cleaner').length;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          {t('user_management')}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => handleOpenUserDialog()}
        >
          {t('add_user')}
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AdminPanelSettings sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('administrators')}
                  </Typography>
                  <Typography variant="h4">
                    {adminUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SupervisorAccount sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('property_managers')}
                  </Typography>
                  <Typography variant="h4">
                    {propertyManagerUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Person sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('staff')}
                  </Typography>
                  <Typography variant="h4">
                    {staffUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CleaningServices sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('cleaners')}
                  </Typography>
                  <Typography variant="h4">
                    {cleanerUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('all_users')}
          </Typography>
          
          {usersLoading ? (
            <Skeleton variant="rectangular" height={400} />
          ) : users.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('user')}</TableCell>
                    <TableCell>{t('email')}</TableCell>
                    <TableCell>{t('phone')}</TableCell>
                    <TableCell>{t('role')}</TableCell>
                    <TableCell>{t('property_assignments')}</TableCell>
                    <TableCell>{t('created')}</TableCell>
                    <TableCell>{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {user.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.name}
                            </Typography>
                            {user.id === currentUser?.id && (
                              <Chip
                                label={t('you')}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Email sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          {user.email}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Phone sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          {user.phone || t('na')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role || 'unknown'}
                          color={getRoleColor(user.role || 'unknown') as any}
                          icon={getRoleIcon(user.role || 'unknown')}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {/* Display regular property assignments for staff/managers */}
                          {user.assigned_properties && user.assigned_properties.length > 0 && (
                            user.assigned_properties.map((assignment) => (
                              <Chip
                                key={assignment.id}
                                label={`${assignment.property?.name || 'Property'} (${assignment.assignment_type})`}
                                size="small"
                                variant="outlined"
                                color={assignment.assignment_type === 'manager' ? 'warning' : 'info'}
                                onDelete={() => handleRemoveProperty(user.id, assignment.property_id)}
                                deleteIcon={<RemoveIcon />}
                                sx={{ mb: 0.5 }}
                              />
                            ))
                          )}
                          
                          {/* Display cleaner property assignments */}
                          {user.cleaner_properties && user.cleaner_properties.length > 0 && (
                            user.cleaner_properties.map((property) => (
                              <Chip
                                key={`cleaner-${property.id}`}
                                label={`${property.name} (cleaner)`}
                                size="small"
                                variant="outlined"
                                color="success"
                                sx={{ mb: 0.5 }}
                              />
                            ))
                          )}
                          
                          {/* Show "No assignments" if no assignments exist */}
                          {(!user.assigned_properties || user.assigned_properties.length === 0) && 
                           (!user.cleaner_properties || user.cleaner_properties.length === 0) && (
                            <Typography variant="body2" color="text.secondary">
                              {t('no_assignments')}
                            </Typography>
                          )}
                          
                          {/* Assignment buttons */}
                          {(user.role === 'property_manager' || user.role === 'staff') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenPropertyAssignDialog(user)}
                              title={t('assign_property')}
                            >
                              <AssignmentIcon />
                            </IconButton>
                          )}
                          
                          {user.role === 'cleaner' && (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenCleanerAssignDialog(user)}
                              title={t('assign_properties_to_cleaner')}
                            >
                              <AssignmentIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewUser(user)}
                            title={t('view_user_details')}
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenUserDialog(user)}
                            title={t('edit_user')}
                          >
                            <EditIcon />
                          </IconButton>
                          {currentUser?.role === 'admin' && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={user.id === currentUser?.id}
                              title={t('delete_user')}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              {t('no_users_found')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* User Dialog */}
      <Dialog open={openUserDialog} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmitUser)}>
          <DialogTitle>
            {editingUser ? t('edit_user') : t('add_new_user')}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('full_name')}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="email"
                      label={t('email_address')}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('phone_number')}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label={t('role')}
                      error={!!errors.role}
                      helperText={errors.role?.message}
                    >
                      <MenuItem value="admin">{t('administrator')}</MenuItem>
                      <MenuItem value="property_manager">{t('property_manager')}</MenuItem>
                      <MenuItem value="staff">{t('staff')}</MenuItem>
                      <MenuItem value="cleaner">{t('cleaner')}</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="password"
                      label={editingUser ? t('new_password') : t('password')}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="password_confirmation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="password"
                      label={t('confirm_password')}
                      error={!!errors.password_confirmation}
                      helperText={errors.password_confirmation?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUserDialog}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createUserMutation.isPending || updateUserMutation.isPending}
            >
              {editingUser ? t('update_user') : t('create_user')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('user_details')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {viewingUser?.name}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('email')}:</strong> {viewingUser?.email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('phone')}:</strong> {viewingUser?.phone || t('na')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('role')}:</strong> {viewingUser?.role}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('created')}:</strong> {formatDate(viewingUser?.created_at)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>
            {t('property_assignments')}
          </Typography>
          <List>
            {viewingUser?.assigned_properties && viewingUser.assigned_properties.length > 0 ? (
              viewingUser.assigned_properties.map((assignment) => (
                <ListItem key={assignment.id}>
                  <ListItemText
                    primary={assignment.property?.name || 'Unknown Property'}
                    secondary={`Assignment: ${assignment.assignment_type}`}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary="No property assignments"
                  secondary=""
                />
              </ListItem>
            )}
          </List>

          {viewingUser?.role === 'cleaner' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Cleaner Properties
              </Typography>
              <List>
                {viewingUser.cleaner_properties && viewingUser.cleaner_properties.length > 0 ? (
                  viewingUser.cleaner_properties.map((property) => (
                    <ListItem key={property.id}>
                      <ListItemText
                        primary={property.name}
                        secondary={`${property.type} • ${property.address} • ${property.city || ''}`}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No properties assigned to cleaner"
                      secondary=""
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Property Assignment Dialog */}
      <Dialog 
        open={openPropertyAssignDialog} 
        onClose={handleClosePropertyAssignDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign Properties to {selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assignment Type</InputLabel>
              <Select
                value={assignmentType}
                label="Assignment Type"
                onChange={(e) => setAssignmentType(e.target.value as 'manager' | 'staff')}
              >
                <MenuItem value="manager">Manager (Full access except delete)</MenuItem>
                <MenuItem value="staff">Staff (Booking management only)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="h6" gutterBottom>
            Available Properties
          </Typography>
          
          {propertiesLoading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : (
            <List>
              {properties
                .filter(property => 
                  !selectedUser?.assigned_properties?.some(
                    assignment => assignment.property_id === property.id
                  )
                )
                .map((property) => (
                  <ListItem key={property.id}>
                    <ListItemText
                      primary={property.name}
                      secondary={`${property.type} • ${property.address}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HomeIcon />}
                        onClick={() => handleAssignProperty(property.id)}
                        disabled={assignPropertyMutation.isPending}
                      >
                        Assign as {assignmentType}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              {properties.filter(property => 
                !selectedUser?.assigned_properties?.some(
                  assignment => assignment.property_id === property.id
                )
              ).length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  All properties are already assigned to this user
                </Typography>
              )}
            </List>
          )}

          {selectedUser?.assigned_properties && selectedUser.assigned_properties.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Current Assignments
              </Typography>
              <List>
                {selectedUser.assigned_properties.map((assignment) => (
                  <ListItem key={assignment.id}>
                    <ListItemText
                      primary={assignment.property?.name || 'Unknown Property'}
                      secondary={`Assignment: ${assignment.assignment_type}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<RemoveIcon />}
                        onClick={() => handleRemoveProperty(selectedUser.id, assignment.property_id)}
                        disabled={removePropertyMutation.isPending}
                      >
                        Remove
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePropertyAssignDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cleaner Assignment Dialog */}
      <Dialog 
        open={openCleanerAssignDialog} 
        onClose={handleCloseCleanerAssignDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CleaningServices color="success" />
            Assign Properties to Cleaner: {selectedUser?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select properties that this cleaner will be responsible for cleaning.
          </Typography>

          {propertiesLoading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : (
            <List>
              {properties.map((property) => (
                <ListItem key={property.id}>
                  <ListItemText
                    primary={property.name}
                    secondary={`${property.type} • ${property.address} • ${property.city || ''}`}
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedProperties.includes(property.id)}
                          onChange={(e) => handlePropertySelectionChange(property.id, e.target.checked)}
                          color="success"
                        />
                      }
                      label="Assign"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {properties.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No properties available
                </Typography>
              )}
            </List>
          )}

          {selectedProperties.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="success.dark">
                Selected Properties ({selectedProperties.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {selectedProperties.map(propertyId => {
                  const property = properties.find(p => p.id === propertyId);
                  return (
                    <Chip
                      key={propertyId}
                      label={property?.name || `Property ${propertyId}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCleanerAssignDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssignCleanerProperties}
            variant="contained"
            color="success"
            disabled={assignCleanerMutation.isPending || selectedProperties.length === 0}
            startIcon={<CleaningServices />}
          >
            {assignCleanerMutation.isPending ? 'Assigning...' : 'Assign Properties'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewIcon />
            User Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingUser && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Basic Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person sx={{ color: 'text.secondary' }} />
                          <Typography>
                            <strong>Name:</strong> {viewingUser.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email sx={{ color: 'text.secondary' }} />
                          <Typography>
                            <strong>Email:</strong> {viewingUser.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone sx={{ color: 'text.secondary' }} />
                          <Typography>
                            <strong>Phone:</strong> {viewingUser.phone || 'Not provided'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getRoleIcon(viewingUser.role || 'unknown')}
                          <Typography>
                            <strong>Role:</strong> 
                            <Chip
                              label={viewingUser.role || 'unknown'}
                              color={getRoleColor(viewingUser.role || 'unknown') as any}
                              size="small"
                              sx={{ ml: 1, textTransform: 'capitalize' }}
                            />
                          </Typography>
                        </Box>
                        <Box>
                          <Typography>
                            <strong>Created:</strong> {formatDate(viewingUser.created_at)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography>
                            <strong>Last Updated:</strong> {formatDate(viewingUser.updated_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Property Assignments
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Regular property assignments */}
                        {viewingUser.assigned_properties && viewingUser.assigned_properties.length > 0 ? (
                          viewingUser.assigned_properties.map((assignment) => (
                            <Box key={assignment.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <HomeIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                              <Typography variant="body2">
                                <strong>{assignment.property?.name || 'Property'}</strong>
                                <Chip
                                  label={assignment.assignment_type}
                                  size="small"
                                  color={assignment.assignment_type === 'manager' ? 'warning' : 'info'}
                                  sx={{ ml: 1, textTransform: 'capitalize' }}
                                />
                              </Typography>
                            </Box>
                          ))
                        ) : null}
                        
                        {/* Cleaner property assignments */}
                        {viewingUser.cleaner_properties && viewingUser.cleaner_properties.length > 0 ? (
                          viewingUser.cleaner_properties.map((property) => (
                            <Box key={`cleaner-${property.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CleaningServices sx={{ color: 'text.secondary', fontSize: 18 }} />
                              <Typography variant="body2">
                                <strong>{property.name}</strong>
                                <Chip
                                  label="cleaner"
                                  size="small"
                                  color="success"
                                  sx={{ ml: 1, textTransform: 'capitalize' }}
                                />
                              </Typography>
                            </Box>
                          ))
                        ) : null}
                        
                        {/* No assignments message */}
                        {(!viewingUser.assigned_properties || viewingUser.assigned_properties.length === 0) && 
                         (!viewingUser.cleaner_properties || viewingUser.cleaner_properties.length === 0) && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No property assignments
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage;
